import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { quoteTimeline, type OperationDetail } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Button } from '@/shared/ui/Button';
import { Card, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../documents/DocumentsPanel';
import { fieldErrorOf } from '../shared/fieldError';
import { areasQuePedem } from './acompanhamento';

/**
 * Aba Documentos da cotacao, nas secoes do OnPoint (COT-089 a COT-100, P-COT-3).
 *
 * "Documentacao do corretor" sao os documentos da corretora da cotacao, so leitura:
 * a seguradora le pela leitura do repositorio, e a corretora le os dela pela leitura
 * propria (P-COR-1), sem ver os de outra. "Documentos inseridos" e o painel comum de
 * documentos da operacao, com a origem e a data de insercao de cada um, o "Upload de
 * documento", a exigencia, a exclusao que guarda o arquivo (P-DOC) e o "Baixar todos".
 * "Documentos excluidos" vem do registro das exclusoes (document_deletions).
 */
export function OperationDocumentsTab({ operacao }: { operacao: OperationDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const [pedindo, setPedindo] = useState(false);

  const baixarTodos = useMutation({
    mutationFn: () => quoteTimeline.documentsArchive(operacao.id),
    onSuccess: (conteudo) => {
      const url = URL.createObjectURL(conteudo);
      const ancora = document.createElement('a');
      ancora.href = url;
      ancora.download = `documentos-da-operacao-${operacao.number}.zip`;
      document.body.append(ancora);
      ancora.click();
      ancora.remove();
      URL.revokeObjectURL(url);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const acoes = (
    <>
      {can('documentos.documento.view') ? (
        <Button
          size="sm"
          variant="secondary"
          loading={baixarTodos.isPending}
          onClick={() => baixarTodos.mutate()}
        >
          Baixar todos
        </Button>
      ) : null}
      {can('documentos.documento.create') && !operacao.is_terminal ? (
        <Button size="sm" variant="secondary" onClick={() => setPedindo(true)}>
          Solicitar ao corretor
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-5">
      {operacao.broker ? (
        <DocumentsPanel
          ownerType="corretor"
          ownerId={operacao.broker.id}
          title="Documentacao do corretor"
          description={`Documentos da corretora ${operacao.broker.label}, so leitura. Sao mantidos no cadastro do corretor.`}
          allowUpload={false}
          allowRequire={false}
          showDeleted={false}
          emptyTitle="Nenhum documento da corretora"
          emptyDescription="A corretora ainda nao tem documento no cadastro."
        />
      ) : (
        <Card>
          <CardHeader title="Documentacao do corretor" />
          <EmptyState
            icon="file"
            title="Cotacao sem corretora"
            description="A operacao e direta: nao ha documentacao de corretor para mostrar."
          />
        </Card>
      )}

      <DocumentsPanel
        ownerType="operacao"
        ownerId={operacao.id}
        title="Documentos inseridos"
        description="Nome, origem e data de insercao de cada documento da cotacao, com as versoes."
        group="Operacao"
        allowDelete
        deleteOnlyWithFile
        showDeleted
        extraAction={acoes}
        emptyTitle="Nenhum documento inserido"
        emptyDescription="Use Upload de documento para inserir, ou Exigir documento para registrar o que falta."
      />

      {pedindo ? (
        <DocumentRequestDialog
          operationId={operacao.id}
          onClose={() => setPedindo(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Pedido de documento ao corretor (SaveProposalRequestDocument do OnPoint): o documento
 * nasce exigido na cotacao, com o comentario, a data e quem pediu, e o pedido entra na
 * linha do tempo. O aviso ao corretor ainda nao sai: a regra de envio esta pendente
 * (FLX-16).
 */
export function DocumentRequestDialog({
  operationId,
  onClose,
}: {
  operationId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [area, setArea] = useState('');
  const [comentario, setComentario] = useState('');

  const pedir = useMutation({
    mutationFn: () =>
      quoteTimeline.requestDocument(operationId, {
        title: titulo.trim(),
        note: comentario.trim(),
        ...(area ? { area } : {}),
      }),
    onSuccess: () => {
      toast.success('Documento solicitado ao corretor.');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      void queryClient.invalidateQueries({ queryKey: ['operations', operationId] });
      onClose();
    },
  });
  const erro = pedir.error instanceof ApiError ? pedir.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Solicitar documento ao corretor"
      description="O documento nasce exigido na cotacao. O aviso ao corretor ainda nao sai: a regra de envio esta pendente."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            loading={pedir.isPending}
            disabled={titulo.trim() === ''}
            onClick={() => pedir.mutate()}
          >
            Solicitar documento
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pedir.error && typeof erro?.details?.field !== 'string' ? (
          <p className="text-sm text-danger-700">{pedir.error.message}</p>
        ) : null}
        <Field
          label="Documento solicitado"
          required
          value={titulo}
          placeholder="Balanco patrimonial do ultimo exercicio"
          error={fieldErrorOf(erro, 'title')}
          onChange={(event) => setTitulo(event.target.value)}
        />
        <Select
          label="Area que pede"
          placeholder="Sem area"
          options={areasQuePedem}
          value={area}
          error={fieldErrorOf(erro, 'area')}
          onChange={(event) => setArea(event.target.value)}
        />
        <Textarea
          label="Comentario do pedido"
          value={comentario}
          rows={3}
          maxLength={2000}
          error={fieldErrorOf(erro, 'note')}
          onChange={(event) => setComentario(event.target.value)}
        />
      </div>
    </Modal>
  );
}
